/**
 * Hook to fetch Google Ads accounts with names from the API
 * Used to display real account names in Activity Report for unlinked accounts
 * Includes automatic auth error detection and re-authentication flow
 */

import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useCallback, useState } from 'react'

export interface GoogleAdsAccount {
  customerId: string
  name: string
  currencyCode?: string
  timeZone?: string
  isManager: boolean
}

export interface GoogleAdsAccountsResponse {
  accounts: GoogleAdsAccount[]
  accountsById: Record<string, { name: string; isManager: boolean }>
  totalAccounts: number
  errors?: Array<{ customerId: string; error: string }>
}

interface GoogleAdsErrorResponse {
  error: boolean
  message: string
  details?: string[]
  requestId?: string
  authError?: boolean
  authUrl?: string
}

const fetchGoogleAdsAccounts = async (): Promise<GoogleAdsAccountsResponse> => {
  try {
    const response = await axios.get<GoogleAdsAccountsResponse>(`${baseUrl}/api/activity-tracker/google-ads/accounts`)
    return response.data
  } catch (error) {
    const errorData = error.response?.data as GoogleAdsErrorResponse | undefined

    // Create enhanced error with all available details
    const enhancedError = new Error(errorData?.message || error.message) as any
    enhancedError.isAuthError = errorData?.authError === true
    enhancedError.authUrl = errorData?.authUrl
    enhancedError.details = errorData?.details || []
    enhancedError.requestId = errorData?.requestId
    throw enhancedError
  }
}

export interface UseGoogleAdsAccountsOptions {
  enabled?: boolean
}

export const useGoogleAdsAccounts = (options: UseGoogleAdsAccountsOptions = {}) => {
  const { enabled = true } = options
  const [authWindowOpen, setAuthWindowOpen] = useState(false)

  const query = useQuery({
    queryKey: ['googleAdsAccounts'],
    queryFn: fetchGoogleAdsAccounts,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - accounts don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: (failureCount, error: any) => {
      // Don't retry auth errors - user needs to re-authenticate
      if (error?.isAuthError) return false
      return failureCount < 1
    },
  })

  // Check if the error is an auth error
  const isAuthError = (query.error as any)?.isAuthError === true

  // Start the re-authentication flow
  const startReAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/activity-tracker/google-ads/auth/start`)
      const { authUrl } = response.data

      // Open auth URL in popup window
      const popup = window.open(authUrl, 'Google Ads Authentication', 'width=600,height=700,scrollbars=yes')

      setAuthWindowOpen(true)

      // Listen for success message from popup
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'GOOGLE_ADS_AUTH_SUCCESS') {
          setAuthWindowOpen(false)
          window.removeEventListener('message', handleMessage)
          // Refetch accounts after successful auth
          query.refetch()
        }
      }
      window.addEventListener('message', handleMessage)

      // Also check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed)
          setAuthWindowOpen(false)
          window.removeEventListener('message', handleMessage)
          // Try refetching anyway in case auth succeeded
          setTimeout(() => query.refetch(), 1000)
        }
      }, 500)
    } catch (error) {
      console.error('Failed to start Google Ads re-authentication:', error)
      setAuthWindowOpen(false)
    }
  }, [query])

  // Extract error details for display
  const errorMessage = (query.error as any)?.message || ''
  const errorDetails: string[] = (query.error as any)?.details || []

  return {
    accounts: query.data?.accounts ?? [],
    accountsById: query.data?.accountsById ?? {},
    totalAccounts: query.data?.totalAccounts ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    // Auth error handling
    isAuthError,
    authWindowOpen,
    startReAuth,
    // Error details for display
    errorMessage,
    errorDetails,
  }
}

export default useGoogleAdsAccounts
